import { useBoardCamera } from '../components/useBoardCamera.js';
import { Canvas } from "@react-three/fiber";
import {
  ChessBoard,
  boardThemeById,
  pieceMaterialById,
  pieceSizeById,
  pieceThemeById,
  tableThemeByMaterial,
} from "@strategy4kids/board-3d";
import { buildShieldMap, parsePlacement } from "@strategy4kids/game-engine";
import { Icon } from "@strategy4kids/ui";
import { useMemo, useState } from "react";
import { GamePanel } from "../components/GamePanel.js";
import { Minimap } from "../components/Minimap.js";
import { MoveTickers } from "../components/MoveTickers.js";
import { Sidebar } from "../components/Sidebar.js";
import { SquareOverlay } from "../components/SquareOverlay.js";
import { useAssistHints } from "../components/useAssistHints.js";
import { useBoardReady } from "../components/useBoardReady.js";
import { useT } from "../i18n/useT.js";
import { useGameStore } from "../state/game-store.js";
import { useThemeStore } from "../state/theme-store.js";
import { useRobotLine } from "../components/RobotBubble.js";
import {
  CapturedPieces,
  PlayerBar,
  useDuelIdentity,
} from "../components/table/PlayerBar.js";
import {
  RoundSummaryPanel,
  type RoundSummary,
} from "../components/table/RoundSummaryPanel.js";

/** The other colour. Local rather than imported so the intent — "the side we
 *  are NOT describing" — is visible at the call site. */
function opposite(color: "w" | "b"): "w" | "b" {
  return color === "w" ? "b" : "w";
}

export function GameScreen({ onExit }: { onExit: () => void }) {
  const boardCam = useBoardCamera();
  const { t } = useT("games");
  const pos = useGameStore((s) => s.pos);
  const selected = useGameStore((s) => s.selected);
  const targets = useGameStore((s) => s.targets);
  const lastMove = useGameStore((s) => s.lastMove);
  const checkSquare = useGameStore((s) => s.checkSquare);
  const reveal = useGameStore((s) => s.reveal);
  const heatmap = useGameStore((s) => s.heatmap);
  const robotThinking = useGameStore((s) => s.robotThinking);
  const phase = useGameStore((s) => s.phase);
  const predictedReply = useGameStore((s) => s.predictedReply);
  const pieceThemeId = useThemeStore((s) => s.pieceThemeId);
  const boardThemeId = useThemeStore((s) => s.boardThemeId);
  const tableMaterialId = useThemeStore((s) => s.tableMaterialId);
  const tableToneId = useThemeStore((s) => s.tableToneId);
  const pieceMaterialId = useThemeStore((s) => s.pieceMaterialId);
  const pieceSizeId = useThemeStore((s) => s.pieceSizeId);
  const pieceWhiteColor = useThemeStore((s) => s.pieceWhiteColor);
  const pieceBlackColor = useThemeStore((s) => s.pieceBlackColor);
  const showCoords = useThemeStore((s) => s.showCoords);
  const showMinimap = useThemeStore((s) => s.showMinimap);
  const [boardReady, markBoardReady] = useBoardReady();
  const [overlayTransform, setOverlayTransform] = useState<string>();
  const botLine = useRobotLine();
  const who = useDuelIdentity(t("table-you"), t("table-opponent"));

  // The opponent's last move drives the 3D arrow. We use the same
  // `adapter.history(pos)` trick MoveTickers.tsx uses (so the array reference
  // is stable between renders) and pick the most recent move by black —
  // the default robot color when the player plays white. Black-player
  // setups don't get the arrow; that's the audit's assumption anyway and
  // matches the MoveTicker split (top=opponent strip).
  const adapter = useGameStore((s) => s.adapter);
  const moves = useMemo(() => adapter.history(pos), [adapter, pos]);

  // Derived, not stored. `pushState()` is only reached from `move()` and
  // `robotMove()` — `startGame` and `undo` write `pos` directly — so a shield
  // map kept in the store would need three write sites, and missing one shows
  // a map from a *different* position. Recomputing here makes that class of
  // staleness structurally impossible. Same reasoning as the `history` memo
  // directly above.
  const placement = useMemo(() => parsePlacement(pos.fen), [pos]);
  const playerColor = useGameStore((s) => s.playerColor);
  // Two maps, both cheap (~260 edges each). The first is the always-on
  // readout and is scoped to the *human's* pieces — not `pos.turn`, which
  // alternates and would repaint the board in the robot's colours every ply.
  // The second exists purely so that revealing an enemy square can answer
  // "what is protecting that piece?", which the viewer's map structurally
  // cannot: it only records the viewer as a subject.
  const shields = useMemo(
    () => buildShieldMap(placement, adapter.attackGen(), playerColor),
    [adapter, placement, playerColor],
  );
  const enemyShields = useMemo(
    () => buildShieldMap(placement, adapter.attackGen(), opposite(playerColor)),
    [adapter, placement, playerColor],
  );
  // Only on the human's turn: while the robot thinks (or the game is over)
  // there is nothing for the player to move, so ringing pieces would lie.
  const { movable, paths, unsafe, strongTargets } = useAssistHints(
    adapter,
    pos,
    selected,
    targets,
    phase !== "gameover" && !robotThinking && pos.turn === playerColor,
  );
  const revealedPiece = reveal ? adapter.pieceAt(pos, reveal) : null;
  const revealShields =
    revealedPiece && revealedPiece.color !== playerColor
      ? enemyShields
      : shields;

  // Checkers only: which crown row the promotion band marks. Chess games pass
  // null so neither the 3D band nor the DOM mirror mounts. `playerColor` (not
  // `pos.turn` — same reasoning as the shield maps above) because the band
  // marks the *human's* goal, which does not alternate by ply.
  const promotionRow = adapter.gameId === "checkers" ? playerColor : null;

  const lastMoveEntry = moves.at(-1);
  const opponentArrow =
    lastMoveEntry && lastMoveEntry.color === "b"
      ? { from: lastMoveEntry.from, to: lastMoveEntry.to }
      : null;

  const opponentColor = opposite(playerColor);
  const gameOver = phase === "gameover" && pos.result != null;
  const winner =
    pos.result && "winner" in pos.result ? pos.result.winner : null;
  const outcome = winner ? (winner === playerColor ? "win" : "lose") : "draw";
  const headline =
    pos.result?.kind === "checkmate"
      ? winner === "w"
        ? t("checkmate-you-win")
        : t("checkmate-robot-wins")
      : winner
        ? winner === "w"
          ? t("you-win")
          : t("robot-wins")
        : t("draw");
  const mark = (side: "w" | "b") =>
    outcome === "draw" ? "½" : winner === side ? "1" : "0";
  const summary: RoundSummary = {
    round: 1,
    outcome,
    headline,
    gameOver: true,
    rows: [
      {
        key: "you",
        label: who.you.name,
        portrait: who.you.portrait,
        delta: "",
        total: mark(playerColor),
        you: true,
        leading: winner === playerColor,
      },
      {
        key: "opponent",
        label: who.opponent.name,
        portrait: who.opponent.portrait,
        delta: "",
        total: mark(opponentColor),
        leading: winner === opponentColor,
      },
    ],
  };
  const capturedBy = (side: "w" | "b") =>
    (pos.captured[opposite(side)] ?? []) as string[];

  return (
    <main
      className="mx-auto flex w-full max-w-[1500px] flex-col items-stretch gap-2 p-4 pt-2 sm:gap-4 sm:pt-4 lg:flex-row"
      data-appdom-scope="chess-game"
    >
      <Sidebar>
        <GamePanel onExit={onExit} />
      </Sidebar>
      <section className="relative order-first min-w-0 flex-1 lg:order-none">
        <div className="mx-auto flex w-full flex-col gap-2 lg:max-w-[max(22rem,calc(100dvh-20rem))]">
          <PlayerBar
            testid="player-bar-opponent"
            name={who.opponent.name}
            portrait={who.opponent.portrait}
            fallbackIcon={who.opponent.fallbackIcon}
            active={phase === "playing" && pos.turn === opponentColor}
            bubble={botLine}
          >
            <CapturedPieces
              pieces={capturedBy(opponentColor)}
              label={t("table-captured")}
              testid="captured-opponent"
            />
          </PlayerBar>
          <div className="mb-1">
            <MoveTickers />
          </div>
          <div
            className="relative aspect-square w-full rounded-3xl bg-board-frame max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:max-w-none max-sm:overflow-hidden max-sm:rounded-none shadow-xl"
            data-appdom-id="chess-board"
            data-appdom-type="Board"
          >
            <Canvas
              dpr={[1, 2]}
              camera={boardCam}
              onCreated={markBoardReady}
            >
              <ChessBoard
                fen={pos.fen}
                selected={selected}
                targets={targets}
                lastMove={lastMove}
                checkSquare={checkSquare}
                movable={movable}
                paths={paths}
                heatmap={heatmap}
                pieceTheme={pieceThemeById(pieceThemeId)}
                boardTheme={boardThemeById(boardThemeId)}
                tableTheme={tableThemeByMaterial(tableMaterialId, tableToneId)}
                pieceMaterial={pieceMaterialById(pieceMaterialId)}
                pieceSize={pieceSizeById(pieceSizeId)}
                pieceWhiteColor={pieceWhiteColor}
                pieceBlackColor={pieceBlackColor}
                showCoords={showCoords}
                predictedReply={predictedReply}
                opponentArrow={opponentArrow}
                shields={shields}
                reveal={reveal}
                revealShields={revealShields}
                promotionRow={promotionRow}
                onOverlayTransform={setOverlayTransform}
              />
            </Canvas>
            <SquareOverlay
              selected={selected}
              targets={targets}
              movable={movable}
              paths={paths}
              unsafe={unsafe}
              strongTargets={strongTargets}
              transform={overlayTransform}
              onTap={(sq) => useGameStore.getState().tap(sq)}
              ready={boardReady}
              predictedReply={predictedReply}
              opponentArrow={opponentArrow}
              shields={shields}
              reveal={reveal}
              revealShields={revealShields}
              promotionRow={promotionRow}
            />
            {showMinimap && <Minimap fen={pos.fen} />}
            {(robotThinking || phase === "gameover") && (
              <div
                className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-3xl bg-ink/50 text-2xl font-extrabold text-cream"
                data-testid="game-over"
                data-appdom-id="game-over"
                data-appdom-type="Banner"
              >
                <span className="inline-flex">
                  {pos.result &&
                  pos.result.kind !== "stalemate" &&
                  pos.result.kind !== "draw" ? (
                    (pos.result.kind === "checkmate" &&
                      pos.result.winner === "w") ||
                    (pos.result.kind === "resign" &&
                      pos.result.winner === "w") ? (
                      <Icon name="PartyPopper" size="lg" className="text-sun" />
                    ) : null
                  ) : null}
                </span>
                <span>
                  {pos.result?.kind === "checkmate"
                    ? pos.result.winner === "w"
                      ? t("checkmate-you-win")
                      : t("checkmate-robot-wins")
                    : pos.result && "winner" in pos.result
                      ? pos.result.winner === "w"
                        ? t("you-win")
                        : t("robot-wins")
                      : pos.result
                        ? t("draw")
                        : robotThinking
                          ? t("robot-thinking")
                          : ""}
                </span>
              </div>
            )}
          </div>
          <PlayerBar
            testid="player-bar-you"
            name={who.you.name}
            portrait={who.you.portrait}
            fallbackIcon={who.you.fallbackIcon}
            you
            active={phase === "playing" && pos.turn === playerColor}
          >
            <CapturedPieces
              pieces={capturedBy(playerColor)}
              label={t("table-captured")}
              testid="captured-you"
            />
          </PlayerBar>
          {gameOver && (
            <RoundSummaryPanel
              testid="game-summary"
              summary={summary}
              nextLabel={t("table-play-again")}
              nextTestid="game-summary-again"
              onNext={() => useGameStore.getState().restart()}
            />
          )}
        </div>
      </section>
    </main>
  );
}
